// "Dati Elemento 1" / "Dati Elemento 2": material (tipo/specie/classe or custom name, or the CLT sub-menu), grain
// orientation, ε, continuous element, geometry, densities, ETA exceptions and the minimum distances. Visibility,
// disabled state, auto-filled values and warnings come from the engine (HTML updateVisibility & co.).
import { memo, useId, type FC } from "react";
import { FormField } from "../../../components/ds2/FormField";
import { Input } from "../../../components/ds2/Input";
import { MessageBox } from "../../../components/ds2/MessageBox";
import { Numeric } from "../../../components/ds2/Numeric";
import { Select } from "../../../components/ds2/Select";
import { Switch } from "../../../components/ds2/Switch";
import { useReadOnly } from "../../../components/ui/ReadOnlyGate";
import useI18n from "../../../hooks/useTranslations";
import type { ConnectionType } from "../../../http/screwConnector/formulas";
import Html from "./Html";
import { useThreeLabels } from "./labels";
import { useMissing } from "./missingFields";
import Note from "./Note";
import { numOrNull, stringOptions, type EngineView } from "./uiState";
import { useNumberDraft } from "./useNumberDraft";
import { useScForm } from "./useScForm";
import CltFields from "./CltFields";
import styles from "./ElementCard.module.css";

export type MaterialLevel = "tipo" | "specie" | "classe";

type ElementCardProps = {
  n: 1 | 2;
  view: EngineView;
  // a material select changed: the page resolves the catalog row (fallback specie/classe) and the DB densities
  onMaterialChange?: (n: 1 | 2, level: MaterialLevel, value: string) => void;
  // t{n} changed: particleboard ρk depends on the thickness (changeThickness)
  onThicknessChange?: (n: 1 | 2, thickness: number | null) => void;
};

// number controls of an element read through `num`
type NumberControl = `${"b" | "d" | "spessore" | "rhok" | "rhomean" | "eps" | "a4t" | "a4c" | "a1e" | "a2e"}${1 | 2}`;

const FIBER_OPTIONS = [
  { value: "0", label: "0°" },
  { value: "90", label: "90°" },
];

const ElementCard: FC<ElementCardProps> = ({ n, view, onMaterialChange, onThicknessChange }) => {
  const disabled = useReadOnly();
  const calc = useI18n().translation.screwConnectorModule.calculation;
  const t = calc.element;
  const notes = calc.notes;
  const switchId = useId();
  const { values, set } = useScForm();
  const draft = useNumberDraft();
  const isMissing = useMissing();
  const { ui, options } = view;
  const ev = view.values;
  const connectionType: ConnectionType = ev?.materiale1 ?? values.connectionType;
  const labels = useThreeLabels(connectionType);

  // effective value of a number control: the engine's (as the HTML DOM shows it) or, without engine, the form's
  const num = (id: NumberControl, formValue: number | null) => (ev ? numOrNull(ev[id]) : formValue);

  const materialSelect = (level: MaterialLevel, value: string) => onMaterialChange?.(n, level, value);
  const tipo = ev ? ev[`mat${n}Tipo` as const] : "";
  const specie = ev ? ev[`mat${n}Specie` as const] : "";
  const classe = ev ? ev[`mat${n}Classe` as const] : "";
  const tipoOptions = stringOptions(options.matTipo[n]);
  const specieOptions = stringOptions(options.matSpecie[n]);
  const classeOptions = stringOptions(options.matClasse[n]);

  const continuous = ev ? ev[`continuo${n}` as const] : values[`element${n}Continuous` as const];
  const epsAuto = ev ? ev[`eps${n}Auto` as const] : values[`element${n}ScrewGrainAngleAuto` as const];
  const fiber = String(ev ? ev[`fi${n}` as const] : (values[`element${n}FiberOrientation` as const] ?? 0));

  const eps = num(`eps${n}` as const, values[`element${n}ScrewGrainAngle` as const]);
  const b = num(`b${n}` as const, values[`element${n}Width` as const]);
  const d = num(`d${n}` as const, values[`element${n}Depth` as const]);
  const thickness = num(`spessore${n}` as const, values[`element${n}Thickness` as const]);
  const rhok = num(`rhok${n}` as const, values[`element${n}CharacteristicDensity` as const]);
  const rhomean = num(`rhomean${n}` as const, values[`element${n}MeanDensity` as const]);
  // the engine's warnings in place of the field's info footer
  const dWarn = ui.texts[`d${n}Warn` as const];
  const rhomeanWarn = ui.visible[`rhomean${n}Warn` as const] ? t.rhomeanMissing : null;
  const a4reduceWarn = ui.texts[`a4reduce${n}Warn` as const];

  return (
    <>
      <FormField label={t.materialType} footer={{ type: "info", message: t.materialTypeUnit }} disabled={disabled}>
        {({ labelId, disabled }) => (
          <Select
            inputId={labelId}
            name={`mat${n}Tipo`}
            options={tipoOptions}
            value={tipoOptions.find((o) => o.value === tipo) ?? null}
            isDisabled={disabled}
            isInvalid={isMissing(`mat${n}Tipo`, !tipo)}
            isSearchable={false}
            placeholder=""
            onChange={(o) => o && o.value !== tipo && materialSelect("tipo", o.value)}
          />
        )}
      </FormField>

      {ui.visible[`mat${n}NormalFields` as const] ? (
        <div className={styles.stack}>
          {ui.visible[`mat${n}SpecieClasseRow` as const] ? (
            <div className={styles.row}>
              {n === 2 || ui.visible.mat1SpecieField ? (
                <FormField label={t.species} disabled={disabled}>
                  {({ labelId, disabled }) => (
                    <Select
                      inputId={labelId}
                      name={`mat${n}Specie`}
                      options={specieOptions}
                      value={specieOptions.find((o) => o.value === specie) ?? null}
                      isDisabled={disabled}
                      isInvalid={isMissing(`mat${n}Specie`, !specie)}
                      isSearchable={false}
                      placeholder=""
                      onChange={(o) => o && o.value !== specie && materialSelect("specie", o.value)}
                    />
                  )}
                </FormField>
              ) : null}
              <FormField label={t.class} disabled={disabled}>
                {({ labelId, disabled }) => (
                  <Select
                    inputId={labelId}
                    name={`mat${n}Classe`}
                    options={classeOptions}
                    value={classeOptions.find((o) => o.value === classe) ?? null}
                    isDisabled={disabled}
                    isInvalid={isMissing(`mat${n}Classe`, !classe)}
                    isSearchable={false}
                    placeholder=""
                    onChange={(o) => o && o.value !== classe && materialSelect("classe", o.value)}
                  />
                )}
              </FormField>
            </div>
          ) : null}
          {ui.visible[`mat${n}CustomNameField` as const] ? (
            <FormField label={t.customName} footer={{ type: "info", message: t.customNameUnit }} disabled={disabled}>
              {({ labelId, disabled }) => (
                <Input
                  id={labelId}
                  placeholder={t.customNamePlaceholder}
                  value={
                    (ev ? ev[`mat${n}CustomName` as const] : values[`element${n}CustomMaterialName` as const]) ?? ""
                  }
                  disabled={disabled}
                  onChange={(e) => set(`element${n}CustomMaterialName` as const, e.currentTarget.value || null)}
                />
              )}
            </FormField>
          ) : null}
          {n === 2 || ui.visible.mat1RhoHint ? (
            <div>
              <Html text={t.rhoHint} />
              {ui.visible[`mat${n}Warn` as const] ? (
                <>
                  {" "}
                  <Html text={t.rhoMissing} className={styles.inlineWarn} />
                </>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {ui.visible[`mat${n}CltFields` as const] ? <CltFields n={n} view={view} /> : null}

      {ui.visible[`fi${n}Field` as const] ? (
        <FormField
          label={t.fiber(n)}
          footer={{ type: "info", message: n === 1 ? t.fiberUnit1 : labels.fiber2Unit }}
          disabled={disabled}
        >
          {({ labelId, disabled }) => (
            <Select
              inputId={labelId}
              name={`fi${n}`}
              options={FIBER_OPTIONS}
              value={FIBER_OPTIONS.find((o) => o.value === fiber) ?? null}
              isDisabled={disabled}
              isInvalid={isMissing(`fi${n}`, !fiber)}
              isSearchable={false}
              placeholder=""
              onChange={(o) =>
                o && o.value !== fiber && set(`element${n}FiberOrientation` as const, o.value === "90" ? 90 : 0)
              }
            />
          )}
        </FormField>
      ) : null}

      {ui.visible[`eps${n}Field` as const] ? (
        <div className={styles.stack}>
          <div className={styles.row}>
            <FormField
              label={t.eps(n)}
              footer={{ type: "info", message: n === 1 ? t.epsUnit1 : labels.eps2Unit }}
              disabled={disabled || ui.disabled[`eps${n}` as const]}
            >
              {({ labelId, disabled }) => (
                <Numeric
                  id={labelId}
                  name={`eps${n}`}
                  disabled={disabled}
                  isInvalid={isMissing(`eps${n}`, eps === null)}
                  {...draft("eps", eps, (v) => set(`element${n}ScrewGrainAngle` as const, v))}
                />
              )}
            </FormField>
          </div>
          <Switch
            id={`${switchId}-epsAuto`}
            checked={epsAuto}
            disabled={disabled}
            onChange={(v) => set(`element${n}ScrewGrainAngleAuto` as const, v)}
          >
            {/* label and description: two items of the Switch's flex row, as in the HTML's .checkline */}
            <Html text={t.epsAuto(n)} />{" "}
            <span>{n === 1 ? t.epsAutoUnit1 : t.epsAutoUnit2}</span>
          </Switch>
          <Note html={ui.texts[`eps${n}Note` as const]} />
        </div>
      ) : null}

      <Switch
        id={`${switchId}-continuous`}
        checked={continuous}
        disabled={disabled || ui.disabled[`continuo${n}` as const]}
        onChange={(v) => set(`element${n}Continuous` as const, v)}
      >
        {t.continuous} <span>{t.continuousUnit}</span>
      </Switch>
      {ui.visible[`continuo${n}Note` as const] ? <Note html={notes.continuous(n)} /> : null}
      {ui.visible[`continuo${n}DisabledNote` as const] ? (
        <MessageBox type="warning">{notes.continuousDisabled}</MessageBox>
      ) : null}

      <div className={styles.row}>
        <FormField
          label={`b${n}`}
          footer={{ type: "info", message: "mm" }}
          disabled={disabled || (n === 2 && ui.disabled.b2)}
        >
          {({ labelId, disabled }) => (
            <Numeric
              id={labelId}
              name={`b${n}`}
              disabled={disabled}
              isInvalid={isMissing(`b${n}`, b === null)}
              {...draft("b", b, (v) => set(`element${n}Width` as const, v))}
            />
          )}
        </FormField>
        {ui.visible[`d${n}Field` as const] ? (
          <FormField
            label={`d${n}`}
            footer={dWarn ? { type: "warning", message: dWarn } : { type: "info", message: "mm" }}
            disabled={disabled || (n === 2 && ui.disabled.d2)}
          >
            {({ labelId, disabled }) => (
              <Numeric
                id={labelId}
                name={`d${n}`}
                disabled={disabled}
                isInvalid={isMissing(`d${n}`, d === null)}
                {...draft("d", d, (v) => set(`element${n}Depth` as const, v))}
              />
            )}
          </FormField>
        ) : null}
      </div>

      <div className={styles.row}>
        <FormField
          label={t.thickness(n)}
          footer={{ type: "info", message: "mm" }}
          disabled={disabled || ui.disabled[`spessore${n}` as const]}
        >
          {({ labelId, disabled }) => (
            <Numeric
              id={labelId}
              name={`spessore${n}`}
              disabled={disabled}
              isInvalid={isMissing(`spessore${n}`, thickness === null)}
              {...draft("thickness", thickness, (v) =>
                onThicknessChange ? onThicknessChange(n, v) : set(`element${n}Thickness` as const, v),
              )}
            />
          )}
        </FormField>
        {n === 2 || ui.visible.rhok1Field ? (
          <FormField
            label="ρk"
            footer={{ type: "info", message: "kg/m³" }}
            disabled={disabled || ui.disabled[`rhok${n}` as const]}
          >
            {({ labelId, disabled }) => (
              <Numeric
                id={labelId}
                name={`rhok${n}`}
                disabled={disabled}
                isInvalid={isMissing(`rhok${n}`, rhok === null)}
                {...draft("rhok", rhok, (v) => set(`element${n}CharacteristicDensity` as const, v))}
              />
            )}
          </FormField>
        ) : null}
      </div>

      {n === 2 || ui.visible.rhomean1Row ? (
        <div className={styles.row}>
          <FormField
            label="ρₘₑₐₙ"
            footer={rhomeanWarn ? { type: "warning", message: rhomeanWarn } : { type: "info", message: t.rhomeanUnit }}
            disabled={disabled}
          >
            {({ labelId, disabled }) => (
              <Numeric
                id={labelId}
                name={`rhomean${n}`}
                disabled={disabled}
                isInvalid={isMissing(`rhomean${n}`, rhomean === null)}
                {...draft("rhomean", rhomean, (v) => set(`element${n}MeanDensity` as const, v))}
              />
            )}
          </FormField>
        </div>
      ) : null}

      {n === 2 || ui.visible.douglas1Field ? (
        <Switch
          id={`${switchId}-douglas`}
          checked={ev ? ev[`douglas${n}` as const] : values[`element${n}DouglasFir` as const]}
          disabled={disabled}
          onChange={(v) => set(`element${n}DouglasFir` as const, v)}
        >
          {t.douglas} <span>{t.douglasUnit}</span>
        </Switch>
      ) : null}
      {n === 2 || ui.visible.a4reduce1Field ? (
        <Switch
          id={`${switchId}-a4reduce`}
          checked={ev ? ev[`a4reduce${n}` as const] : values[`element${n}ReduceUnloadedEdgeDistance` as const]}
          disabled={disabled}
          onChange={(v) => set(`element${n}ReduceUnloadedEdgeDistance` as const, v)}
        >
          {t.a4reduce(n)} <span>{n === 1 ? t.a4reduceUnit1 : t.a4reduceUnit2}</span>
        </Switch>
      ) : null}
      {a4reduceWarn ? <MessageBox type="warning">{a4reduceWarn}</MessageBox> : null}

      <div className={styles.row}>
        <FormField
          label={`a4,t,${n}`}
          footer={{ type: "info", message: "mm" }}
          disabled={disabled || ui.disabled[`a4t${n}` as const]}
        >
          {({ labelId, disabled }) => (
            <Numeric
              id={labelId}
              disabled={disabled}
              {...draft("a4t", num(`a4t${n}` as const, values[`element${n}EdgeDistanceA4t` as const]), (v) =>
                set(`element${n}EdgeDistanceA4t` as const, v),
              )}
            />
          )}
        </FormField>
        <FormField
          label={`a4,c,${n}`}
          footer={{ type: "info", message: "mm" }}
          disabled={disabled || ui.disabled[`a4c${n}` as const]}
        >
          {({ labelId, disabled }) => (
            <Numeric
              id={labelId}
              disabled={disabled}
              {...draft("a4c", num(`a4c${n}` as const, values[`element${n}EdgeDistanceA4c` as const]), (v) =>
                set(`element${n}EdgeDistanceA4c` as const, v),
              )}
            />
          )}
        </FormField>
      </div>

      {n === 2 || ui.visible.spacing1FieldRow ? (
        <div className={styles.row}>
          <FormField label={t.a1e(n)} footer={{ type: "info", message: t.a1Unit }} disabled>
            {({ labelId }) => (
              <Numeric
                id={labelId}
                disabled
                value={num(`a1e${n}` as const, values[`element${n}EdgeDistanceA1e` as const])}
              />
            )}
          </FormField>
          <FormField label={t.a2e(n)} footer={{ type: "info", message: t.a2Unit }} disabled>
            {({ labelId }) => (
              <Numeric
                id={labelId}
                disabled
                value={num(`a2e${n}` as const, values[`element${n}EdgeDistanceA2e` as const])}
              />
            )}
          </FormField>
        </div>
      ) : null}

      {n === 1 ? (
        <>
          {ui.visible.steel1SpacingNote ? <Note html={notes.steel1Spacing} /> : null}
          <FormField label={t.a3t1} footer={{ type: "info", message: "mm" }} disabled={disabled || ui.disabled.a3t1}>
            {({ labelId, disabled }) => (
              <Numeric
                id={labelId}
                disabled={disabled}
                {...draft("a3t1", ev ? numOrNull(ev.a3t1) : values.element1EdgeDistanceA3t, (v) =>
                  set("element1EdgeDistanceA3t", v),
                )}
              />
            )}
          </FormField>
          {ui.visible.steel1DistNote ? <Note html={notes.steel1Dist} /> : null}
          <Note html={notes.a3t1Rule} />
        </>
      ) : (
        <div className={styles.row}>
          <FormField
            label="a3,t,2"
            footer={{ type: "info", message: t.a3t2Unit }}
            disabled={disabled || ui.disabled.a3t2}
          >
            {({ labelId, disabled }) => (
              <Numeric
                id={labelId}
                disabled={disabled}
                {...draft("a3t2", ev ? numOrNull(ev.a3t2) : values.element2EdgeDistanceA3t, (v) =>
                  set("element2EdgeDistanceA3t", v),
                )}
              />
            )}
          </FormField>
          <FormField
            label="a3,c,2"
            footer={{ type: "info", message: t.a3c2Unit }}
            disabled={disabled || ui.disabled.a3c2}
          >
            {({ labelId, disabled }) => (
              <Numeric
                id={labelId}
                disabled={disabled}
                {...draft("a3c2", ev ? numOrNull(ev.a3c2) : values.element2EdgeDistanceA3c, (v) =>
                  set("element2EdgeDistanceA3c", v),
                )}
              />
            )}
          </FormField>
        </div>
      )}
    </>
  );
};

// Memoized: it re-renders through its own props (the engine view among them) and contexts, not whenever its parent
// does.
export default memo(ElementCard);
